import React, { useState, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, RotateCcw } from 'lucide-react';

interface AdditionObject {
  id: string;
  emoji: string;
  name: string;
  color: string;
}

interface AdditionProblem {
  id: string;
  question: string;
  leftGroup: number;
  rightGroup: number;
  answer: number;
}

interface AdditionManipulatorResource {
  type: 'addition_manipulator';
  name: string;
  title: string;
  description: string;
  maxNumber: number;
  objects: AdditionObject[];
  problems: AdditionProblem[];
  instructions: string;
  showAnswer: boolean;
  allowDrag: boolean;
}

interface AdditionManipulatorComponentProps {
  resource: AdditionManipulatorResource;
  onAction?: (action: string, data: any) => void;
}

export const AdditionManipulatorComponent: React.FC<AdditionManipulatorComponentProps> = ({
  resource,
  onAction
}) => {
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [leftGroup, setLeftGroup] = useState<AdditionObject[]>([]);
  const [rightGroup, setRightGroup] = useState<AdditionObject[]>([]);
  const [availableObjects, setAvailableObjects] = useState<AdditionObject[]>([]);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [completedProblems, setCompletedProblems] = useState<Set<number>>(new Set());

  const currentProblem = resource.problems[currentProblemIndex];

  // Initialiser les objets dans les groupes
  useEffect(() => {
    const totalObjects = currentProblem.leftGroup + currentProblem.rightGroup;
    const objects = [];
    for (let i = 0; i < totalObjects; i++) {
      objects.push({
        ...resource.objects[i % resource.objects.length],
        id: `${resource.objects[i % resource.objects.length].id}_${i}`
      });
    }
    
    // Diviser les objets entre les groupes
    const leftObjects = objects.slice(0, currentProblem.leftGroup);
    const rightObjects = objects.slice(currentProblem.leftGroup, currentProblem.leftGroup + currentProblem.rightGroup);
    const remainingObjects = objects.slice(currentProblem.leftGroup + currentProblem.rightGroup);
    
    setLeftGroup(leftObjects);
    setRightGroup(rightObjects);
    setAvailableObjects(remainingObjects);
    
    // Vérifier si c'est correct dès le début
    if (leftObjects.length === currentProblem.leftGroup && rightObjects.length === currentProblem.rightGroup) {
      setIsCorrect(true);
      setShowFeedback(true);
      setCompletedProblems(prev => new Set(prev).add(currentProblemIndex));
    } else {
      setIsCorrect(null);
      setShowFeedback(false);
    }
  }, [currentProblemIndex, resource.objects, currentProblem]);

  const moveObject = useCallback((objectId: string, targetGroup: 'left' | 'right' | 'available') => {
    if (!resource.allowDrag) return;

    const object = availableObjects.find(obj => obj.id === objectId) || 
                   leftGroup.find(obj => obj.id === objectId) || 
                   rightGroup.find(obj => obj.id === objectId);
    if (!object) return;

    // Retirer l'objet de sa position actuelle
    setAvailableObjects(prev => prev.filter(obj => obj.id !== objectId));
    setLeftGroup(prev => prev.filter(obj => obj.id !== objectId));
    setRightGroup(prev => prev.filter(obj => obj.id !== objectId));

    // Ajouter l'objet au bon groupe
    if (targetGroup === 'left') {
      setLeftGroup(prev => [...prev, object]);
    } else if (targetGroup === 'right') {
      setRightGroup(prev => [...prev, object]);
    } else {
      // Retourner l'objet dans les objets disponibles
      setAvailableObjects(prev => [...prev, object]);
    }

    // Vérifier si c'est correct seulement si on ajoute à un groupe
    if (targetGroup === 'left' || targetGroup === 'right') {
      const newLeftCount = targetGroup === 'left' ? leftGroup.length + 1 : leftGroup.length;
      const newRightCount = targetGroup === 'right' ? rightGroup.length + 1 : rightGroup.length;
      
      if (newLeftCount === currentProblem.leftGroup && newRightCount === currentProblem.rightGroup) {
        setIsCorrect(true);
        setShowFeedback(true);
        setCompletedProblems(prev => new Set(prev).add(currentProblemIndex));
        
        if (onAction) {
          onAction('problem_completed', {
            problemId: currentProblem.id,
            attempts: attempts + 1,
            isCorrect: true
          });
        }
      } else {
        setIsCorrect(false);
        setShowFeedback(true);
        setAttempts(prev => prev + 1);
      }
    }
  }, [leftGroup, rightGroup, currentProblem, attempts, onAction, resource.allowDrag, availableObjects, currentProblemIndex]);

  const resetProblem = useCallback(() => {
    const totalObjects = currentProblem.leftGroup + currentProblem.rightGroup;
    const objects = [];
    for (let i = 0; i < totalObjects; i++) {
      objects.push({
        ...resource.objects[i % resource.objects.length],
        id: `${resource.objects[i % resource.objects.length].id}_${i}`
      });
    }
    
    // Diviser les objets entre les groupes
    const leftObjects = objects.slice(0, currentProblem.leftGroup);
    const rightObjects = objects.slice(currentProblem.leftGroup, currentProblem.leftGroup + currentProblem.rightGroup);
    const remainingObjects = objects.slice(currentProblem.leftGroup + currentProblem.rightGroup);
    
    setLeftGroup(leftObjects);
    setRightGroup(rightObjects);
    setAvailableObjects(remainingObjects);
    setIsCorrect(null);
    setShowFeedback(false);
    setAttempts(0);
  }, [currentProblem, resource.objects]);

  const nextProblem = useCallback(() => {
    if (currentProblemIndex < resource.problems.length - 1) {
      setCurrentProblemIndex(prev => prev + 1);
    }
  }, [currentProblemIndex, resource.problems.length]);

  const previousProblem = useCallback(() => {
    if (currentProblemIndex > 0) {
      setCurrentProblemIndex(prev => prev - 1);
    }
  }, [currentProblemIndex]);

  const getObjectStyle = (color: string) => {
    const colorMap: { [key: string]: string } = {
      red: 'bg-red-100 border-red-300 text-red-800',
      yellow: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      orange: 'bg-orange-100 border-orange-300 text-orange-800',
      purple: 'bg-purple-100 border-purple-300 text-purple-800',
      blue: 'bg-blue-100 border-blue-300 text-blue-800',
      green: 'bg-green-100 border-green-300 text-green-800'
    };
    return colorMap[color] || 'bg-gray-100 border-gray-300 text-gray-800';
  };

  const isProblemComplete = leftGroup.length === currentProblem.leftGroup && 
                           rightGroup.length === currentProblem.rightGroup;

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{resource.title}</h2>
        <p className="text-gray-600 mb-4">{resource.description}</p>
        <p className="text-sm text-gray-500">{resource.instructions}</p>
      </div>

      {/* Problème actuel */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          Problème {currentProblemIndex + 1} sur {resource.problems.length}
        </h3>
        <p className="text-blue-800 mb-2">{currentProblem.question}</p>
        <div className="flex items-center justify-center space-x-4 text-sm text-blue-700">
          <span>Groupe de gauche : {currentProblem.leftGroup}</span>
          <span>+</span>
          <span>Groupe de droite : {currentProblem.rightGroup}</span>
          <span>=</span>
          <span className="font-bold">{currentProblem.answer}</span>
        </div>
      </div>

      {/* Zones de manipulation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Groupe de gauche */}
        <div className="bg-green-50 rounded-lg p-4 min-h-[200px]">
          <h4 className="text-center font-semibold text-green-800 mb-3">
            Groupe de gauche ({leftGroup.length}/{currentProblem.leftGroup})
          </h4>
          <div className="flex flex-wrap gap-2 justify-center min-h-[120px]">
            {leftGroup.map((obj, index) => (
              <div
                key={`left-${obj.id}-${index}`}
                className={`w-12 h-12 flex items-center justify-center text-2xl rounded-lg border-2 cursor-pointer transition-all hover:scale-105 ${getObjectStyle(obj.color)}`}
                onClick={() => moveObject(obj.id, 'available')}
                title={`Cliquer pour retirer`}
              >
                {obj.emoji}
              </div>
            ))}
          </div>
        </div>

        {/* Objets disponibles */}
        <div className="bg-gray-50 rounded-lg p-4 min-h-[200px]">
          <h4 className="text-center font-semibold text-gray-800 mb-3">
            Objets disponibles ({availableObjects.length})
          </h4>
          <div className="flex flex-wrap gap-2 justify-center min-h-[120px]">
            {availableObjects.map((obj, index) => (
              <div
                key={`available-${obj.id}-${index}`}
                className={`w-12 h-12 flex items-center justify-center text-2xl rounded-lg border-2 cursor-pointer transition-all hover:scale-105 ${getObjectStyle(obj.color)}`}
                onClick={() => {
                  // Déterminer automatiquement le groupe cible
                  const targetGroup = leftGroup.length < currentProblem.leftGroup ? 'left' : 'right';
                  moveObject(obj.id, targetGroup);
                }}
                title={`Cliquer pour ajouter au groupe ${leftGroup.length < currentProblem.leftGroup ? 'gauche' : 'droit'}`}
              >
                {obj.emoji}
              </div>
            ))}
          </div>
        </div>

        {/* Groupe de droite */}
        <div className="bg-blue-50 rounded-lg p-4 min-h-[200px]">
          <h4 className="text-center font-semibold text-blue-800 mb-3">
            Groupe de droite ({rightGroup.length}/{currentProblem.rightGroup})
          </h4>
          <div className="flex flex-wrap gap-2 justify-center min-h-[120px]">
            {rightGroup.map((obj, index) => (
              <div
                key={`right-${obj.id}-${index}`}
                className={`w-12 h-12 flex items-center justify-center text-2xl rounded-lg border-2 cursor-pointer transition-all hover:scale-105 ${getObjectStyle(obj.color)}`}
                onClick={() => moveObject(obj.id, 'available')}
                title={`Cliquer pour retirer`}
              >
                {obj.emoji}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Feedback */}
      {showFeedback && (
        <div className={`text-center p-4 rounded-lg mb-4 ${
          isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {isCorrect ? (
            <div className="flex items-center justify-center space-x-2">
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">Correct ! Bravo !</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <XCircle className="w-5 h-5" />
              <span>Pas encore ! Vérifiez vos groupes.</span>
            </div>
          )}
        </div>
      )}

      {/* Réponse si activée */}
      {resource.showAnswer && isProblemComplete && (
        <div className="bg-yellow-50 rounded-lg p-4 mb-4">
          <div className="text-center">
            <p className="text-yellow-800 font-semibold">
              {leftGroup.length} + {rightGroup.length} = {leftGroup.length + rightGroup.length}
            </p>
          </div>
        </div>
      )}

      {/* Contrôles */}
      <div className="flex justify-center space-x-4">
        <button
          onClick={resetProblem}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Réinitialiser</span>
        </button>
        
        {currentProblemIndex > 0 && (
          <button
            onClick={previousProblem}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Précédent
          </button>
        )}
        
        {currentProblemIndex < resource.problems.length - 1 && (
          <button
            onClick={nextProblem}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Suivant
          </button>
        )}
      </div>

      {/* Progression */}
      <div className="mt-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progression</span>
          <span>{completedProblems.size}/{resource.problems.length} problèmes complétés</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(completedProblems.size / resource.problems.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default AdditionManipulatorComponent;
